import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api, formatApiError } from "@/lib/api";
import Nav from "@/components/Nav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Database, Play, Loader2, Star, TrendingUp, Gauge, Users, RefreshCw, Terminal, Code2 } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);
const plus = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x.toISOString().slice(0, 10); };

export default function AdminMarketData() {
  const { t, lang, user, properties, activeId } = useApp();
  const nav = useNavigate();
  const active = (properties || []).find((p) => p.id === activeId) || (properties || [])[0];
  const [f, setF] = useState({
    city: "", latitude: undefined, longitude: undefined,
    check_in: plus(today(), 14), check_out: plus(today(), 16),
    adults: 2, children: 0, radius_km: 5, max_items: 30, force: false,
  });
  const [status, setStatus] = useState("idle"); // idle|running|ready|error
  const [data, setData] = useState(null);
  const [showJson, setShowJson] = useState(false);
  const [trendStatus, setTrendStatus] = useState(null);
  const pollRef = useRef(null);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    api.get("/admin/search-trend/status")
      .then(({ data }) => setTrendStatus(data))
      .catch(() => {});
  }, []);

  // Prefill location from the active property (fallback to Rimini if none).
  useEffect(() => {
    const city = active?.city || active?.market?.city;
    setF((s) => ({
      ...s,
      city: city || "Rimini",
      latitude: active?.latitude ?? (city ? s.latitude : 44.0678),
      longitude: active?.longitude ?? (city ? s.longitude : 12.5695),
    }));
  }, [active?.id]);


  if (user && user.role !== "admin") {
    return (
      <div className="App min-h-screen"><Nav />
        <div className="max-w-lg mx-auto px-6 py-32 text-center">
          <Database className="w-10 h-10 text-primary mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">403</h1>
          <p className="text-muted-foreground">Solo amministratori.</p>
        </div>
      </div>
    );
  }

  const poll = (cacheKey) => {
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/market-data/status?cache_key=${cacheKey}`);
        if (data.status === "ready") { clearInterval(pollRef.current); setData(data); setStatus("ready"); }
        else if (data.status === "error") { clearInterval(pollRef.current); setData(data); setStatus("error"); toast.error("Errore Apify"); }
      } catch { /* keep polling */ }
    }, 5000);
  };

  const run = async (force) => {
    if (pollRef.current) clearInterval(pollRef.current);
    setStatus("running"); setData(null);
    try {
      const { data } = await api.post("/admin/market-data/test", { ...f, force });
      if (data.status === "ready") { setData(data); setStatus("ready"); }
      else if (data.cache_key) { poll(data.cache_key); }
    } catch (e) {
      setStatus("error"); toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const a = data?.analysis || {};
  const p = a.prices || {};
  const items = data?.items || [];
  const Stat = ({ icon: Icon, label, value, color }) => (
    <div className="bg-secondary/40 border border-border rounded-lg p-3">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground font-bold mb-1">
        <Icon className="w-3.5 h-3.5" />{label}</div>
      <div className="text-2xl font-data font-bold" style={{ color }}>{value ?? "—"}</div>
    </div>
  );

  return (
    <div className="App min-h-screen"><Nav />
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-start gap-3 mb-6">
          <div className="w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"><Database className="w-6 h-6 text-primary" /></div>
          <div>
            <h1 className="text-2xl font-bold leading-tight">Market Data Test</h1>
            <p className="text-sm text-muted-foreground">Apify · voyager/booking-scraper — Market Intelligence provider</p>
          </div>
        </div>

        {/* form */}
        <div className="bg-card border border-border rounded-lg p-5 mb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <div className="col-span-2"><Label>Città</Label><Input data-testid="mdt-city" value={f.city} onChange={(e) => set("city", e.target.value)} className="mt-1" /></div>
            <div><Label>Lat</Label><Input data-testid="mdt-lat" type="number" value={f.latitude} onChange={(e) => set("latitude", parseFloat(e.target.value))} className="mt-1" /></div>
            <div><Label>Lon</Label><Input data-testid="mdt-lon" type="number" value={f.longitude} onChange={(e) => set("longitude", parseFloat(e.target.value))} className="mt-1" /></div>
            <div><Label>Check-in</Label><Input data-testid="mdt-checkin" type="date" value={f.check_in} onChange={(e) => set("check_in", e.target.value)} className="mt-1" /></div>
            <div><Label>Check-out</Label><Input data-testid="mdt-checkout" type="date" value={f.check_out} onChange={(e) => set("check_out", e.target.value)} className="mt-1" /></div>
            <div><Label>Adulti</Label><Input data-testid="mdt-adults" type="number" min="1" value={f.adults} onChange={(e) => set("adults", Number(e.target.value))} className="mt-1" /></div>
            <div><Label>Bambini</Label><Input data-testid="mdt-children" type="number" min="0" value={f.children} onChange={(e) => set("children", Number(e.target.value))} className="mt-1" /></div>
            <div><Label>Raggio (km)</Label>
              <select data-testid="mdt-radius" value={f.radius_km} onChange={(e) => set("radius_km", Number(e.target.value))} className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
                {[1, 3, 5, 10].map((r) => <option key={r} value={r}>{r} km</option>)}
              </select></div>
            <div><Label>Max risultati</Label><Input data-testid="mdt-max" type="number" min="5" max="100" value={f.max_items} onChange={(e) => set("max_items", Number(e.target.value))} className="mt-1" /></div>
          </div>
          <div className="flex items-center gap-3 mt-4">
            <Button data-testid="mdt-run" onClick={() => run(false)} disabled={status === "running"}>
              {status === "running" ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Analisi in corso...</> : <><Play className="w-4 h-4 mr-2" />Esegui test</>}
            </Button>
            <Button data-testid="mdt-refresh" variant="outline" onClick={() => run(true)} disabled={status === "running"}>
              <RefreshCw className="w-4 h-4 mr-2" />Aggiorna dati di mercato
            </Button>
            {data?.cached && <span className="text-xs text-muted-foreground">da cache ({data.cache_age_hours}h fa)</span>}
          </div>
        </div>

        {/* Trend di ricerca (Google Trends) — SOLO ADMIN: solo data ultima verifica.
            Risultati e keyword nascosti; aggiornamento solo automatico settimanale. */}
        <div className="bg-card border border-border rounded-lg p-5 mb-6" data-testid="search-trend-status">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-4 h-4 text-primary" />
            <h2 className="text-base font-bold">Trend di ricerca (Google Trends)</h2>
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            Fattore interno del pricing (peso limitato). Aggiornamento automatico: {trendStatus?.schedule || "settimanale · lunedì 04:10"}. Nessun aggiornamento manuale; risultati e keyword nascosti agli utenti.
          </p>
          {trendStatus?.items?.length ? (
            <div className="divide-y divide-border border border-border rounded-md">
              {trendStatus.items.map((it) => (
                <div key={it.destination_key} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`trend-row-${it.destination_key}`}>
                  <span className="font-medium">{it.city}</span>
                  <span className="text-muted-foreground text-xs">
                    Ultima verifica: {it.last_verified_at ? new Date(it.last_verified_at).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" }) : "mai"}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Nessuna verifica ancora registrata. La prima verrà eseguita al prossimo aggiornamento automatico notturno.</p>
          )}
        </div>

        {status === "running" && (
          <div data-testid="mdt-loading" className="text-center py-16 text-muted-foreground">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-primary" />
            <p>Chiamata ad Apify in corso… (può richiedere 30–90 secondi)</p>
          </div>
        )}

        {status === "ready" && (
          <div className="space-y-6" data-testid="mdt-results">
            {/* stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              <Stat icon={Users} label="Competitor" value={a.competitor_count} />
              <Stat icon={Gauge} label="Market Pulse" value={a.market_pulse} color="var(--primary)" />
              <Stat icon={Star} label="Rating medio" value={a.avg_rating} />
              <Stat icon={TrendingUp} label="Quality medio" value={a.avg_quality_score} />
              <Stat icon={TrendingUp} label="Prezzo medio" value={p.avg ? `€${p.avg}` : "—"} />
              <Stat icon={TrendingUp} label="Mediana" value={p.median ? `€${p.median}` : "—"} color="var(--demand-low)" />
              <Stat icon={TrendingUp} label="Min–Max" value={p.min ? `€${p.min}–${p.max}` : "—"} />
            </div>

            {/* distribution */}
            {p.distribution && (
              <div className="bg-card border border-border rounded-lg p-5">
                <p className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground mb-3">Distribuzione prezzi (dev.std €{p.stdev}, outlier rimossi {p.outliers_removed})</p>
                <div className="flex items-end gap-2 h-28">
                  {p.distribution.map((b, i) => {
                    const max = Math.max(...p.distribution.map((x) => x.count)) || 1;
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center justify-end">
                        <div className="w-full rounded-t bg-primary/70" style={{ height: `${(b.count / max) * 100}%` }} />
                        <span className="text-[10px] text-muted-foreground mt-1">{b.range}</span>
                        <span className="text-[10px] font-data font-bold">{b.count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* table */}
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="px-5 py-3 border-b border-border text-[11px] uppercase tracking-widest font-bold text-muted-foreground">Tabella dati ({items.length})</div>
              <div className="overflow-x-auto max-h-[460px]">
                <table className="w-full text-sm" data-testid="mdt-table">
                  <thead className="bg-secondary/60 sticky top-0"><tr className="text-left">
                    {["Nome", "Tipo", "€/notte", "Stelle", "Rating", "Rec.", "Q", "Dist", "Servizi"].map((h) => <th key={h} className="px-3 py-2 font-semibold whitespace-nowrap">{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {items.map((it, i) => (
                      <tr key={i} data-testid={`mdt-row-${i}`} className="border-t border-border hover:bg-accent/40">
                        <td className="px-3 py-2 max-w-[220px] truncate"><a href={it.url} target="_blank" rel="noreferrer" className="hover:underline text-primary">{it.name}</a></td>
                        <td className="px-3 py-2">{it.type}</td>
                        <td className="px-3 py-2 font-data font-semibold">{it.price_per_night ? `€${it.price_per_night}` : "—"}</td>
                        <td className="px-3 py-2">{it.stars || "—"}</td>
                        <td className="px-3 py-2">{it.rating || "—"}</td>
                        <td className="px-3 py-2">{it.reviews || "—"}</td>
                        <td className="px-3 py-2 font-data">{it.quality_score}</td>
                        <td className="px-3 py-2 font-data">{it.distance_km != null ? `${it.distance_km}km` : "—"}</td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">
                          {[it.pool && "piscina", it.spa && "spa", it.parking && "parking", it.sea_view && "vista mare", it.breakfast && "colazione", it.pets_allowed && "pet"].filter(Boolean).join(", ") || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* apify log */}
            <div className="bg-card border border-border rounded-lg p-5">
              <div className="flex items-center gap-2 mb-2 text-muted-foreground"><Terminal className="w-4 h-4" /><span className="text-[11px] uppercase tracking-widest font-bold">Log chiamata Apify</span></div>
              <pre data-testid="mdt-log" className="text-xs bg-secondary/50 rounded p-3 overflow-x-auto font-data">{JSON.stringify(data.log, null, 2)}</pre>
            </div>

            {/* raw json */}
            <div className="bg-card border border-border rounded-lg p-5">
              <button data-testid="mdt-json-toggle" onClick={() => setShowJson((s) => !s)} className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
                <Code2 className="w-4 h-4" /><span className="text-[11px] uppercase tracking-widest font-bold">Risposta JSON completa {showJson ? "▲" : "▼"}</span></button>
              {showJson && <pre className="text-xs bg-secondary/50 rounded p-3 overflow-auto max-h-[500px] mt-3 font-data">{JSON.stringify(data, null, 2)}</pre>}
            </div>
          </div>
        )}

        {status === "error" && (
          <div data-testid="mdt-error" className="bg-destructive/10 border border-destructive/30 rounded-lg p-5 text-sm">
            <p className="font-semibold text-destructive mb-2">Errore durante la chiamata Apify</p>
            <pre className="text-xs overflow-x-auto">{JSON.stringify(data?.log, null, 2)}</pre>
          </div>
        )}
      </div>
    </div>
  );
}
